import {
  UseQueryOptions,
  UseQueryResult,
  useQuery,
} from '@tanstack/react-query';
import type {
  DomainEntity,
  GetEndpointResponse,
  EntityDetail,
  EnvironmentKey,
  ConfigEntityMeta,
} from '@web-config-app/core';
import { useEnvironmentFetch } from '../use-environment-fetch/use-environment-fetch';
import { getEntityGetQueryConfig } from './entity-get-query';

type EntityGetUseQueryResult = UseQueryResult<GetEndpointResponse>;

type RefetchOnOption = boolean | 'always' | undefined;

export type UseEntityGetResult = Omit<EntityGetUseQueryResult, 'data'> & {
  /**
   * There's a clash between `useQuery` returning a `data` property and our endpoints that ALSO include a `data` property
   * in the response. So, to avoid have `data.data`, hoist the endpoint's `response.data` to the top level here.
   */
  data?: EntityDetail;
  included?: EntityDetail[];
  meta?: ConfigEntityMeta;
};

/**
 * Exactly the options this hook honours, rather than extending Partial<UseQueryOptions> and
 * forwarding a subset.
 *
 * The wider type was a promise the implementation didn't keep -- anything outside
 * fetchFn/enabled/retry was silently dropped, so `select` or `refetchInterval` did nothing.
 * An unsupported option is now a type error. Add a field here, and forward it below.
 */
export interface UseEntityGetOptions {
  fetchFn?: (path: string, options: any) => Promise<Response>;
  getIncludedEntities?: boolean;
  isUsingConfigAppBackend?: boolean;
  enabled?: boolean;
  retry?: UseQueryOptions['retry'];
  staleTime?: number;
  gcTime?: number;
  refetchOnWindowFocus?: RefetchOnOption;
  refetchOnMount?: RefetchOnOption;
  refetchOnReconnect?: RefetchOnOption;
}

interface UseEntityGetProps {
  entity: DomainEntity;
  instanceId?: string;
  environment: EnvironmentKey;
  options?: UseEntityGetOptions;
}

/**
 * useEntityGet - The following hook is responsible for calling the BE API associated to the Get (GET) service of an entity.
 * @param props
 * @param props.entity - the whole {@link Entity} that exposes the needed values including the id, endpoints, etc.
 * @param props.instanceId - the entity ID needed to call the GET api with.
 * @param props.options - optional config object
 * @param props.options.fetchFn - You can pass in your own custom fetch function to be used, instead of the default currentEnvironmentFetch
 * @returns the result of useQuery, which included the fetching, loading statuses and the data returned.
 */
export const useEntityGet = ({
  entity,
  instanceId,
  environment,
  options,
}: UseEntityGetProps): UseEntityGetResult => {
  const { apiUrl } = entity;

  const { isUsingConfigAppBackend, getIncludedEntities } = options ?? {};

  const {
    fetchFn,
    enabled = true,
    retry,
    staleTime,
    gcTime,
    refetchOnWindowFocus,
    refetchOnMount,
    refetchOnReconnect,
  } = options ?? {};

  const currentEnvironmentFetch = useEnvironmentFetch(environment, apiUrl);

  // Key and fetcher come from the shared config, so any other reader of this entity lands on
  // the same cache entry by construction rather than by keeping a tuple in step.
  const { queryKey, queryFn } = getEntityGetQueryConfig({
    entity,
    instanceId,
    environment,
    getIncludedEntities,
    isUsingConfigAppBackend,
    environmentFetch: fetchFn ?? currentEnvironmentFetch,
  });

  /**
   * Uses the endpoint root URL
   *
   * We rename the data returned by useQuery so we can differentiate the data from our API response which
   * also has a `data` property. This avoids a result with a `result.data.data` structure.
   */

  const { data: queryResultData, ...queryResult } = useQuery({
    // Forwarded one by one, matching UseEntityGetOptions exactly -- see its doc comment.
    staleTime,
    gcTime,
    refetchOnWindowFocus,
    refetchOnMount,
    refetchOnReconnect,
    queryFn,
    queryKey,
    enabled: enabled && Boolean(instanceId),
    retry: retry ?? false,
    // TODO: NEED TO ACCESS WHAT VALUE TO ADD HERE IN THE FUTURE. COMMENTING OUT TO RESOLVE A CACHING ISSUE
    // staleTime: Infinity,
  });

  return {
    ...queryResult,
    data: queryResultData?.data,
    included: queryResultData?.included,
    meta: queryResultData?.meta,
  };
};
